import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { Reveal, SectionHeader } from "@/components/site/Reveal";
import { STAT_VALUES } from "@/data/content";
import { useLanguage } from "@/i18n/LanguageContext";

const Counter = ({ to, suffix }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-80px" });
    const [val, setVal] = useState(0);

    useEffect(() => {
        if (!inView) return;
        const controls = animate(0, to, {
            duration: 2,
            ease: "easeOut",
            onUpdate: (v) => setVal(Math.round(v)),
        });
        return () => controls.stop();
    }, [inView, to]);

    return (
        <span ref={ref} className="tabular-nums">
            {val}
            {suffix}
        </span>
    );
};

export const WhyMe = () => {
    const { t } = useLanguage();

    return (
        <section id="why-me" className="relative py-24 md:py-32 overflow-hidden" data-testid="why-me-section">
            <div
                className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[400px] w-[700px] rounded-full bg-cyan-400/[0.06] blur-[120px]"
                aria-hidden="true"
            />
            <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                <SectionHeader
                    number={t.why.number}
                    label={t.why.label}
                    title={t.why.title}
                    align="center"
                />
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
                    {STAT_VALUES.map((stat, i) => (
                        <Reveal key={t.why.stats[i]} delay={i * 0.1}>
                            <div
                                className="glass-card glass-card-hover rounded-2xl p-8 text-center transition-transform duration-500 hover:-translate-y-2"
                                data-testid={`stat-card-${i}`}
                            >
                                <p className="font-display text-5xl md:text-6xl font-black text-gradient">
                                    <Counter to={stat.value} suffix={stat.suffix} />
                                </p>
                                <p className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">
                                    {t.why.stats[i]}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.2}>
                    <ul className="flex flex-wrap justify-center gap-x-8 gap-y-4 max-w-4xl mx-auto" data-testid="why-points-list">
                        {t.why.points.map((point) => (
                            <li key={point} className="flex items-center gap-2.5 text-sm md:text-base text-zinc-300">
                                <CheckCircle2 className="h-5 w-5 text-cyan-400 shrink-0" strokeWidth={1.75} />
                                {point}
                            </li>
                        ))}
                    </ul>
                </Reveal>
            </div>
        </section>
    );
};
